import {
  CarOutlined,
  FileTextOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "./LoanDetails.scss";
import moment from "moment/moment";
import { useSelector } from "react-redux";
import { formatCurrency } from "../../../../assets/helpers";
import PanelCard from "../../../../Pages/DashboardPage/components/PanelCard";

const EMPTY = "—";

/* Every value used to fall back to the string 'NA', which reads as data.
   Missing values recede instead. */
const orNull = (value) =>
  value === null || value === undefined || value === "" ? null : value;

const asDate = (value) => {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : null;
};

const LoanDetails = ({ data }) => {
  const { format } = useSelector(state => state.priceFormat);

  // const personalDetails = [
  //   {
  //     label: "Customer Mailing Address-1",
  //     value: data?.cust_mailing_addr1 || 'NA',
  //   },
  //   { label: "Customer Mailing Address-2", value: data?.cust_mailing_addr2 || 'NA' },
  //   { label: "Customer Mailing Address-3", value: data?.cust_mailing_addr3 || 'NA' },
  //   { label: "Customer Mailing Address-4", value: data?.cust_mailing_addr4 || 'NA' },
  //   { label: "Customer Mailing City", value: data?.cust_mailing_city || 'NA' },
  //   { label: "Phone Number-2", value: data?.cust_mailing_phone2 || 'NA' },
  //   { label: "Mobile Number", value: data?.cust_mailing_phone1 || "NA" },
  //   { label: "Last Due Date", value: data?.last_due_date ? moment(data?.last_due_date).format('DD-MM-YYYY') : 'NA' },
  //   {
  //     label: "Address-1",
  //     value: data?.address_1 || 'NA',
  //   },
  //   {
  //     label: "Address-2",
  //     value: data?.address_2 || 'NA',
  //   },
  // ];

  /* Grouped rather than one flat run of 17 / 19 / 6 fields. Each group is a
     full row of cards, so the columns line up down the whole page, and the
     spanning fields sit at the end of their group where they cannot punch a
     hole in the middle of it. */
  const personalGroups = [
    {
      title: "Contact",
      fields: [
        { label: "Customer Mobile Number", value: orNull(data?.cust_mobile_no), mono: true },
        /* Phone Number-1 read cust_mailing_phone2, the same field as
           Phone Number-2, so the two rows always showed the same number. */
        { label: "Phone Number-1", value: orNull(data?.cust_mailing_phone1), mono: true },
        { label: "Phone Number-2", value: orNull(data?.cust_mailing_phone2), mono: true },
        { label: "City", value: orNull(data?.city) },
      ],
    },
    {
      title: "Addresses",
      fields: [
        { label: "Permanent Address", value: orNull(data?.permanent_address) },
        { label: "Current Address", value: orNull(data?.current_address) },
        { label: "Office Address", value: orNull(data?.office_address) },
        { label: "Address-1", value: orNull(data?.address_1) },
      ],
    },
    /* Four single-column fields, so each reference is exactly one row of the
       shared grid. A spanning address here left the fourth slot of the row
       above it empty. */
    {
      title: "Reference 1",
      fields: [
        { label: "Name", value: orNull(data?.reference_name_1) },
        { label: "Relation", value: orNull(data?.reference_relation_1) },
        { label: "Phone Number", value: orNull(data?.reference_name_1_phone_number), mono: true },
        { label: "Address", value: orNull(data?.reference_name_1_address) },
      ],
    },
    {
      title: "Reference 2",
      fields: [
        { label: "Name", value: orNull(data?.reference_name_2) },
        { label: "Relation", value: orNull(data?.reference_relation_2) },
        { label: "Phone Number", value: orNull(data?.reference_name_2_phone_number), mono: true },
        { label: "Address", value: orNull(data?.reference_name_2_address) },
      ],
    },
  ];

  const loanGroups = [
    {
      title: "Loan",
      fields: [
        { label: "Loan No", value: orNull(data?.loan_no), mono: true },
        { label: "Loan EMI", value: orNull(data?.loan_emi), mono: true },
        { label: "DPD", value: orNull(data?.dpd), mono: true },
        { label: "Bucket", value: orNull(data?.bucket), mono: true },
      ],
    },
    {
      title: "Amounts",
      fields: [
        { label: "Total Amount Due", value: formatCurrency(data?.amount_overdue, format), mono: true },
        { label: "ODPOS", value: formatCurrency(data?.odpos, format), mono: true },
        { label: "POS", value: formatCurrency(data?.pos, format), mono: true },
        { label: "Disbursal Amount", value: formatCurrency(data?.disbursal_amount, format), mono: true },
      ],
    },
    {
      title: "Dates",
      fields: [
        { label: "Disbursal Date", value: asDate(data?.disbursal_date), mono: true },
        { label: "EMI Start Date", value: asDate(data?.emi_start_date), mono: true },
        { label: "Due Date", value: asDate(data?.due_date), mono: true },
        { label: "Last Due Date", value: asDate(data?.last_due_date), mono: true },
      ],
    },
    {
      title: "Bounce",
      fields: [
        { label: "Bounce Date", value: asDate(data?.bounce_date), mono: true },
        { label: "Bounce Charges", value: formatCurrency(data?.bounce_charges, format), mono: true },
        { label: "Bounce Reason", value: orNull(data?.bounce_reason) },
      ],
    },
    /* Five fields in one group left a single card on a second row with three
       empty slots beside it. Split, each group's short last row now reads as
       the end of a named section. */
    {
      title: "Last contact",
      fields: [
        { label: "Last Contacted Date", value: asDate(data?.last_contacted_date), mono: true },
        { label: "Last Contacted On", value: asDate(data?.last_contacted_on), mono: true },
      ],
    },
    {
      title: "Settlement",
      fields: [
        { label: "Broken Settlement", value: orNull(data?.broken_settlement) },
        { label: "Settlement Rejected", value: orNull(data?.sett_rejected) },
        { label: "Settlement Approved", value: orNull(data?.sett_approved_nil_pyt) },
      ],
    },
  ];

  /* Four specs fill one row, then the description takes a group of its own so
     it gets the full width to read in. Disbursal Date is dropped here - it is
     the same data?.disbursal_date already shown under Loan Details > Dates. */
  const assetGroups = [
    {
      fields: [
        { label: "Customer Model", value: orNull(data?.model) },
        { label: "Engine Number", value: orNull(data?.engine_no), mono: true },
        { label: "Chasis Number", value: orNull(data?.chassis_no), mono: true },
        { label: "Tenure", value: orNull(data?.tenure), mono: true },
      ],
    },
    {
      fields: [
        {
          label: "Asset Description",
          value: orNull(data?.asset_desc),
          full: true,
          prose: true,
        },
      ],
    },
  ];

  const renderGroups = (groups) =>
    groups.map(({ title, fields }, index) => (
      <div className="caseGroup" key={title || index}>
        {title && <div className="caseSubhead">{title}</div>}
        <div className="caseGrid">
          {fields.map(({ label, value, mono, prose, full }) => (
            <div
              className={`caseField${full ? " is-full" : ""}`}
              key={label}
            >
              <span className="caseField__label">{label}</span>
              <span
                className={`caseField__value${mono ? " is-mono" : ""}${
                  prose ? " is-prose" : ""
                }${value ? "" : " is-empty"}`}
              >
                {value || EMPTY}
              </span>
            </div>
          ))}
        </div>
      </div>
    ));

  return (
    <div className="caseLoanDetails">
      <PanelCard
        title="Personal Details"
        icon={<UserOutlined />}
        accent="brand"
      >
        {renderGroups(personalGroups)}
      </PanelCard>

      <PanelCard
        title="Loan Details"
        icon={<FileTextOutlined />}
        accent="brand"
      >
        {renderGroups(loanGroups)}
      </PanelCard>

      <PanelCard title="Asset Details" icon={<CarOutlined />} accent="brand">
        {renderGroups(assetGroups)}
      </PanelCard>
    </div>
  );
};

export default LoanDetails;
